import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, Users, ClipboardList, Briefcase, UserCheck, List, Clock, Puzzle, Building, CheckSquare, CalendarDays, Handshake } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SearchResultsModal } from "./SearchResultsModal";
import { useSearch } from "./useSearch";
import { supabase } from "@/integrations/supabase/client";
import { SearchSuggestion } from "./types";
import { useTranslation } from "react-i18next";
import { useEntityHistory, HistoryEntry } from "@/hooks/useEntityHistory";

export const GlobalSearch = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
    const [history, setHistory] = useState<HistoryEntry[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
    const searchRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();
    const { handleSearch, searchModalOpen, setSearchModalOpen, viewMode, setViewMode, searchQuery } = useSearch();
    const { t } = useTranslation();
    const { getRecentHistory } = useEntityHistory();

    const handleKeyDown = (e: KeyboardEvent) => {
        // Open search on Cmd+K or Ctrl+K
        if ((e.metaKey || e.ctrlKey) && e.key === "k") {
            e.preventDefault();
            setIsOpen(prev => !prev);
        }

        // Close on escape
        if (e.key === "Escape" && !searchModalOpen) {
            setIsOpen(false);
        }
    };

    // Handle clicks outside the search component
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen]);

    useEffect(() => {
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [searchModalOpen]);

    // Load history when search is opened
    useEffect(() => {
        if (isOpen && !query.trim()) {
            getRecentHistory().then(setHistory);
        }
    }, [isOpen, query, getRecentHistory]);

    // Fetch suggestions as user types
    useEffect(() => {
        const fetchSuggestions = async () => {
            if (!query.trim()) {
                setSuggestions([]);
                setHighlightedIndex(-1);
                return;
            }

            setIsSearching(true);

            try {
                const { data, error } = await supabase.rpc("global_fuzzy_search", {
                    search_query: query,
                    max_per_type: 5,
                });

                if (error) throw error;

                const formattedSuggestions: SearchSuggestion[] = (data || []).map((row: any) => {
                    const base = { id: row.id, name: row.primary_name, entityType: row.entity_type as SearchSuggestion["entityType"] };
                    switch (row.entity_type) {
                        case "candidate":
                            return { ...base, candidateFunction: row.secondary_text };
                        case "job":
                            return { ...base, companyName: row.secondary_text };
                        case "company":
                            return { ...base };
                        case "contact":
                            return { ...base, companyName: row.secondary_text, companyId: row.metadata?.company_id };
                        case "list":
                            return { ...base, candidateCount: row.metadata?.candidate_count ?? 0 };
                        case "task":
                            return { ...base, taskStatus: row.secondary_text, taskPriority: row.metadata?.priority };
                        case "event":
                            return { ...base, eventStatus: row.secondary_text };
                        case "query":
                            return { ...base, isPublic: row.metadata?.is_public };
                        case "workforce":
                            return { ...base, jobTitle: row.secondary_text, companyName: row.metadata?.company_name };
                        default:
                            return base;
                    }
                });

                setSuggestions(formattedSuggestions);
                setHighlightedIndex(formattedSuggestions.length > 0 ? 0 : -1);
            } catch (error) {
                console.error("Error fetching search suggestions:", error);
            } finally {
                setIsSearching(false);
            }
        };

        const debounceTimer = setTimeout(() => {
            if (query.trim()) {
                fetchSuggestions();
            }
        }, 300);

        return () => clearTimeout(debounceTimer);
    }, [query]);

    // Keyboard navigation for suggestions
    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!isOpen || suggestions.length === 0) return;
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlightedIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : 0));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : suggestions.length - 1));
        } else if (e.key === "Enter") {
            if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
                handleSuggestionClick(suggestions[highlightedIndex]);
            } else {
                onSubmit(e);
            }
        } else if (e.key === "Escape") {
            setIsOpen(false);
        }
    };

    // Handle suggestion click to navigate directly to the entity page
    const handleSuggestionClick = (suggestion: SearchSuggestion) => {
        setIsOpen(false);
        setQuery("");
        setHighlightedIndex(-1);
        // Navigate to the appropriate page based on entity type
        switch (suggestion.entityType) {
            case "candidate":
                navigate(`/candidates/${suggestion.id}`);
                break;
            case "job":
                navigate(`/jobs/${suggestion.id}`);
                break;
            case "company":
                navigate(`/companies/${suggestion.id}`);
                break;
            case "contact":
                // Navigate to the company page that contains this contact
                if (suggestion.companyId) {
                    navigate(`/companies/${suggestion.companyId}`);
                } else {
                    navigate(`/companies`); // Fallback to companies list if no company_id
                }
                break;
            case "query":
                navigate(`/queries/${suggestion.id}`);
                break;
            case "list":
                navigate(`/lists/${suggestion.id}`);
                break;
            case "event":
                navigate(`/calendar?event=${suggestion.id}`);
                break;
            case "match":
                navigate(`/matches/${suggestion.id}`);
                break;
            case "task":
                navigate(`/tasks?task=${suggestion.id}`);
                break;
            case "workforce":
                navigate(`/workforce/${suggestion.id}`);
                break;
            default:
                break;
        }
    };

    // Only open search results modal when Enter is pressed
    const onSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (query.trim()) {
            handleSearch(query);
            setIsOpen(false);
            setHighlightedIndex(-1);
        }
    };

    return (
        <div className="relative flex-1 min-w-[0]" ref={searchRef}>
            <div
                className={cn(
                    "relative flex items-center rounded-sm w-full bg-white dark:bg-background border border-border",
                    isOpen ? "ring-1 ring-primary" : ""
                )}
                onClick={() => setIsOpen(true)}
            >
                <Input
                    type="search"
                    placeholder={t('search.placeholder')}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    className="flex-1 min-w-[160px] max-w-[600px] border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 rounded-sm text-lg px-6 pr-12 py-3"
                    onFocus={() => setIsOpen(true)}
                    onKeyDown={handleInputKeyDown}
                />
                {query && (
                    <Button
                        variant="ghost"
                        size="icon"
                        className="absolute right-2 h-6 w-6 rounded-sm"
                        onClick={(e) => {
                            e.stopPropagation();
                            setQuery("");
                        }}
                    >
                        <X size={14} />
                        <span className="sr-only">{t("common.clearSearch", "Clear search")}</span>
                    </Button>
                )}
            </div>

            {/* Suggestions dropdown (when typing) */}
            {isOpen && query.trim() && (
                <div className="absolute left-0 right-0 mt-2 z-50">
                    <div className="rounded-sm shadow-lg p-2 max-h-60 overflow-y-auto bg-white dark:bg-background border border-border">
                        {isSearching && (
                            <p className="text-sm text-muted-foreground px-2 py-2">{t('search.searching')}</p>
                        )}
                        {!isSearching && suggestions.length > 0 && (
                            <ul className="space-y-1">
                                {suggestions.map((suggestion, idx) => (
                                    <li key={`${suggestion.entityType}-${suggestion.id}`}>
                                        <button
                                            type="button"
                                            className={cn(
                                                "w-full text-left px-3 py-2 hover:bg-accent rounded-sm flex items-center gap-2 transition-colors",
                                                highlightedIndex === idx ? "bg-accent" : ""
                                            )}
                                            onMouseEnter={() => setHighlightedIndex(idx)}
                                            onClick={() => handleSuggestionClick(suggestion)}
                                        >
										{suggestion.entityType === "candidate" && <Users size={14} className="text-blue-500/80" />}
										{suggestion.entityType === "job" && <ClipboardList size={14} className="text-green-500/80" />}
										{suggestion.entityType === "company" && <Briefcase size={14} className="text-purple-500/80" />}
											{suggestion.entityType === "contact" && <UserCheck size={14} className="text-orange-500/80" />}
											{suggestion.entityType === "list" && <List size={14} className="text-gray-500/80" />}
											{suggestion.entityType === "task" && <CheckSquare size={14} className="text-indigo-500/80" />}
											{suggestion.entityType === "query" && <Search size={14} className="text-cyan-500/80" />}
											{suggestion.entityType === "event" && <CalendarDays size={14} className="text-amber-500/80" />}
											{suggestion.entityType === "workforce" && <Handshake size={14} className="text-teal-500/80" />}
											<span>
                                                {suggestion.name}
                                                {suggestion.entityType === "candidate" && suggestion.candidateFunction && (
                                                    <span className="text-xs text-muted-foreground/80 ml-1">
                                                        ({suggestion.candidateFunction})
                                                    </span>
                                                )}
                                                {suggestion.entityType === "job" && suggestion.companyName && (
                                                    <span className="text-xs text-muted-foreground/80 ml-1">
                                                        ({suggestion.companyName})
                                                    </span>
                                                )}
                                                {suggestion.entityType === "task" && suggestion.taskStatus && (
                                                    <span className="text-xs text-muted-foreground/80 ml-1">
                                                        ({suggestion.taskStatus})
                                                    </span>
                                                )}
                                                {suggestion.entityType === "contact" && suggestion.companyName && (
                                                    <span className="text-xs text-muted-foreground/80 ml-1">
                                                        ({suggestion.companyName})
                                                    </span>
                                                )}
                                                {suggestion.entityType === "list" && suggestion.candidateCount !== undefined && (
                                                    <span className="text-xs text-muted-foreground/80 ml-1">
                                                        ({suggestion.candidateCount} candidate{suggestion.candidateCount !== 1 ? 's' : ''})
                                                    </span>
                                                )}
                                                {suggestion.entityType === "workforce" && (suggestion.companyName || suggestion.jobTitle) && (
                                                    <span className="text-xs text-muted-foreground/80 ml-1">
                                                        ({[suggestion.jobTitle, suggestion.companyName].filter(Boolean).join(' - ')})
                                                    </span>
                                                )}
                                            </span>
                                            <span className="text-xs text-muted-foreground ml-auto">
                                                {t(`search.entityTypes.${suggestion.entityType}`)}
                                            </span>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                        {!isSearching && suggestions.length === 0 && (
                            <p className="text-sm text-muted-foreground px-2 py-2">{t('search.noResults')}</p>
                        )}
                    </div>
                </div>
            )}

            {/* History dropdown (when not typing) */}
            {isOpen && !query.trim() && history.length > 0 && (
                <div className="absolute left-0 right-0 mt-2 z-50">
                    <div className="rounded-sm shadow-lg p-2 max-h-60 overflow-y-auto bg-white dark:bg-background border border-border">
                        <div className="px-2 py-1 mb-1 flex items-center gap-2">
                            <Clock size={12} className="text-muted-foreground" />
                            <span className="text-xs text-muted-foreground font-medium">
                                {t('search.recentlyConsulted', 'Recently Consulted')}
                            </span>
                        </div>
                        <ul className="space-y-1">
                            {history.map((entry, idx) => (
                                <li key={`${entry.entity_type}-${entry.entity_id}`}>
                                    <button
                                        type="button"
                                        className={cn(
                                            "w-full text-left px-3 py-2 hover:bg-accent rounded-sm flex items-center gap-2 transition-colors",
                                            highlightedIndex === idx ? "bg-accent" : ""
                                        )}
                                        onMouseEnter={() => setHighlightedIndex(idx)}
                                        onClick={() => {
                                            const suggestion: SearchSuggestion = {
                                                id: entry.entity_id,
                                                name: entry.entity_name,
                                                entityType: entry.entity_type,
                                                companyName: entry.metadata?.companyName,
                                                candidateFunction: entry.metadata?.candidateFunction,
                                                candidateCount: entry.metadata?.candidateCount
                                            };
                                            handleSuggestionClick(suggestion);
                                        }}
                                    >
                                        {entry.entity_type === "candidate" && <Users size={14} className="text-blue-500/80" />}
                                        {entry.entity_type === "job" && <ClipboardList size={14} className="text-green-500/80" />}
                                        {entry.entity_type === "company" && <Briefcase size={14} className="text-purple-500/80" />}
                                        {entry.entity_type === "match" && <Puzzle size={14} className="text-pink-500/80" />}
                                        {entry.entity_type === "list" && <List size={14} className="text-gray-500/80" />}
                                        {entry.entity_type === "task" && <CheckSquare size={14} className="text-indigo-500/80" />}
                                        {entry.entity_type === "query" && <Search size={14} className="text-cyan-500/80" />}
                                        {entry.entity_type === "event" && <CalendarDays size={14} className="text-amber-500/80" />}
                                        {entry.entity_type === "workforce" && <Handshake size={14} className="text-teal-500/80" />}
                                        <span className="flex-1">
                                            {entry.entity_name}
                                            {entry.entity_type === "candidate" && entry.metadata?.candidateFunction && (
                                                <span className="text-xs text-muted-foreground/80 ml-1">
                                                    ({entry.metadata.candidateFunction})
                                                </span>
                                            )}
                                            {(entry.entity_type === "job" || entry.entity_type === "match") && entry.metadata?.companyName && (
                                                <span className="text-xs text-muted-foreground/80 ml-1">
                                                    ({entry.metadata.companyName})
                                                </span>
                                            )}
                                            {entry.entity_type === "list" && entry.metadata?.candidateCount !== undefined && (
                                                <span className="text-xs text-muted-foreground/80 ml-1">
                                                    ({entry.metadata.candidateCount} candidate{entry.metadata.candidateCount !== 1 ? 's' : ''})
                                                </span>
                                            )}
                                            {entry.entity_type === "workforce" && entry.metadata?.companyName && (
                                                <span className="text-xs text-muted-foreground/80 ml-1">
                                                    ({entry.metadata.companyName})
                                                </span>
                                            )}
                                        </span>
                                        <span className="text-xs text-muted-foreground">
                                            {t(`search.entityTypes.${entry.entity_type}`)}
                                        </span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            )}

            {/* Search results modal (unchanged) */}
            <SearchResultsModal
                isOpen={searchModalOpen}
                onOpenChange={setSearchModalOpen}
                searchQuery={searchQuery}
                viewMode={viewMode}
                onViewModeChange={setViewMode}
            />
        </div>
    );
};
